import React, { useState } from 'react';
import './ChatComponents.css';
import MarkdownRenderer from './MarkdownRenderer';

interface MCQOption {
  label: string;
  text: string;
}

interface AnswerResult {
  isCorrect: boolean;
  correctAnswer: string;
  explanation: string;
  followUpPrompts?: Array<{ label: string; prompt: string; category: string }>;
}

interface ChatMCQProps {
  componentId: string;
  questionText: string;
  options: MCQOption[];
  correctAnswer: string;
  explanation?: string;
  topic?: string;
  difficulty?: string;
  onAnswerSubmit: (componentId: string, answer: string) => Promise<AnswerResult | null>;
  onFollowUp?: (prompt: string) => void;
}

const ChatMCQ: React.FC<ChatMCQProps> = ({
  componentId,
  questionText,
  options,
  correctAnswer,
  explanation,
  topic,
  difficulty,
  onAnswerSubmit,
  onFollowUp
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [result, setResult] = useState<AnswerResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOptionClick = (label: string) => {
    if (result) return; // Already answered
    setSelectedAnswer(label);
  };

  const handleSubmit = async () => {
    if (!selectedAnswer || isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Validate client-side — correct answer is already known
      const isCorrect = selectedAnswer === correctAnswer;
      const followUps = isCorrect
        ? [
            { label: 'Another question', prompt: 'Give me another practice question on this topic', category: 'practice' },
            { label: 'Explain more', prompt: 'Can you explain this concept in more detail?', category: 'clarify' }
          ]
        : [
            { label: 'Explain why', prompt: `Why is ${correctAnswer} the correct answer?`, category: 'clarify' },
            { label: 'Try again', prompt: 'Give me a similar question to try again', category: 'practice' }
          ];
      setResult({
        isCorrect,
        correctAnswer,
        explanation: explanation || '',
        followUpPrompts: followUps,
      });

      // Attach the answer to this student + component: records the miss/hit to
      // analytics, feeds topic mastery, and triggers MCQ pre-warm. Fire-and-
      // forget so instant client-side feedback is never blocked on the network.
      if (onAnswerSubmit && componentId) {
        onAnswerSubmit(componentId, selectedAnswer).catch(() => {});
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFollowUp = (prompt: string) => {
    if (onFollowUp) {
      onFollowUp(prompt);
    }
  };

  const getOptionClassName = (label: string) => {
    let className = 'chat-mcq-option';

    if (selectedAnswer === label) {
      className += ' selected';
    }

    if (result) {
      if (label === result.correctAnswer) {
        className += ' correct';
      } else if (selectedAnswer === label) {
        className += ' incorrect';
      }
    }

    return className;
  };

  return (
    <div className="chat-mcq">
      {/* Header with topic/difficulty badges */}
      <div className="chat-mcq-header">
        {topic && <span className="mcq-badge topic">{topic}</span>}
        {difficulty && <span className={`mcq-badge difficulty ${difficulty.toLowerCase()}`}>{difficulty}</span>}
      </div>

      {/* Question text */}
      <div className="chat-mcq-question">
        <MarkdownRenderer>{questionText}</MarkdownRenderer>
      </div>

      {/* Options */}
      <div className="chat-mcq-options">
        {options.map((opt) => (
          <button
            key={opt.label}
            className={getOptionClassName(opt.label)}
            onClick={() => handleOptionClick(opt.label)}
            disabled={!!result}
          >
            <span className="option-label">{opt.label}</span>
            <span className="option-text"><MarkdownRenderer inline>{opt.text}</MarkdownRenderer></span>
            {result && opt.label === result.correctAnswer && (
              <span className="option-icon correct-icon">✓</span>
            )}
            {result && selectedAnswer === opt.label && opt.label !== result.correctAnswer && (
              <span className="option-icon incorrect-icon">✗</span>
            )}
          </button>
        ))}
      </div>

      {/* Submit button (before answer) */}
      {!result && (
        <button
          className="chat-mcq-submit"
          onClick={handleSubmit}
          disabled={!selectedAnswer || isSubmitting}
        >
          {isSubmitting ? 'Checking...' : 'Submit Answer'}
        </button>
      )}

      {/* Result panel (after answer) */}
      {result && (
        <div className={`chat-mcq-result ${result.isCorrect ? 'correct' : 'incorrect'}`}>
          <div className="result-header">
            <span className="result-icon">{result.isCorrect ? '🎉' : '💡'}</span>
            <span className="result-text">
              {result.isCorrect ? 'Correct!' : 'Not quite right'}
            </span>
          </div>
          {result.explanation && (
            <div className="result-explanation">
              <MarkdownRenderer>{result.explanation}</MarkdownRenderer>
            </div>
          )}
          {/* Follow-up prompts */}
          {result.followUpPrompts && result.followUpPrompts.length > 0 && (
            <div className="result-follow-ups">
              {result.followUpPrompts.map((fu, idx) => (
                <button
                  key={idx}
                  className="follow-up-chip"
                  onClick={() => handleFollowUp(fu.prompt)}
                >
                  {fu.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatMCQ;
